import * as THREE from 'three/webgpu';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { CSS2DRenderer } from 'three/addons/renderers/CSS2DRenderer.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { ao } from 'three/addons/tsl/display/GTAONode.js';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import {
  builtinAOContext,
  mix,
  mrt,
  normalView,
  pass,
  screenUV,
  smoothstep,
  uniform,
} from 'three/tsl';
import { EXPLODE_GAP_M } from '../config';
import type { Building, Faded } from './building';
import { MAT, SKY } from './look';
import { crane, surroundings } from './scenery';

const mrtNormals = () => mrt({ output: normalView });

export type PresetName = 'hero' | 'explode' | 'dusk';

interface Preset {
  /** Degrees round the building, and above the horizon. */
  azimuth: number;
  elevation: number;
  explode: number;
  dusk: number;
  /** Multiplies the distance that just fits the building in view. */
  zoom: number;
}

export const PRESETS: Record<PresetName, Preset> = {
  // Every preset looks at the courtyard and sea side, where the restaurants
  // glaze and the terraces are. From +x/+z the plan shows only service cores.
  hero: { azimuth: -128, elevation: 22, explode: 0, dusk: 0, zoom: 0.92 },
  explode: { azimuth: -136, elevation: 18, explode: 1, dusk: 0, zoom: 1.0 },
  dusk: { azimuth: -150, elevation: 12, explode: 0, dusk: 1, zoom: 0.88 },
};

/** The construction slider runs 0 (bare site) to this (finished). */
export const BUILD_MAX = 4;

export interface Pick {
  floor: string;
  code?: string;
}

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const narrow = window.matchMedia('(max-width: 720px)');

/** Frame-rate independent approach to a target. */
const approach = (from: number, to: number, dt: number, rate = 6) =>
  reduceMotion.matches ? to : from + (to - from) * (1 - Math.exp(-rate * dt));

const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const lerp = THREE.MathUtils.lerp;

export class Stage {
  readonly renderer: THREE.WebGPURenderer;
  readonly labels = new CSS2DRenderer();
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(32, 1, 0.5, 3000);
  readonly controls: OrbitControls;
  onPick: (pick: Pick | null) => void = () => {};
  /** Told when the construction slider moves on its own (the arrival, play). */
  onBuild: (value: number, playing: boolean) => void = () => {};

  private readonly hemi = new THREE.HemisphereLight('#dfe9f2', '#8f8a78', 0.8);
  private readonly sun = new THREE.DirectionalLight('#fff0da', 3.2);

  // The backdrop: a gradient drawn behind everything, top to horizon, with
  // fog of the horizon's colour so the land dissolves into it as in the demo.
  private readonly skyTop = uniform(new THREE.Color(SKY.day.top));
  private readonly skyHorizon = uniform(new THREE.Color(SKY.day.horizon));
  private readonly fog = new THREE.FogExp2(SKY.day.fog, 0.0024);
  private readonly palette = {
    dayTop: new THREE.Color(SKY.day.top),
    dayHorizon: new THREE.Color(SKY.day.horizon),
    dayFog: new THREE.Color(SKY.day.fog),
    duskTop: new THREE.Color(SKY.dusk.top),
    duskHorizon: new THREE.Color(SKY.dusk.horizon),
    duskFog: new THREE.Color(SKY.dusk.fog),
    xrayTop: new THREE.Color(SKY.xray.top),
    xrayHorizon: new THREE.Color(SKY.xray.horizon),
    xrayFog: new THREE.Color(SKY.xray.fog),
    sunDay: new THREE.Color('#fff0da'),
    // At night the "sun" is the moon: low, cool and faint, so the building is
    // lit by its own lights.
    sunDusk: new THREE.Color('#9fb4e0'),
    xrayEdge: new THREE.Color(MAT.xray),
    glowAvailable: new THREE.Color(MAT.availableGlow),
    glowLeased: new THREE.Color(MAT.warmLight),
  };
  private readonly scratch = {
    top: new THREE.Color(),
    horizon: new THREE.Color(),
    fog: new THREE.Color(),
  };
  private bloomStrength: { value: number } | null = null;
  private bloomThreshold: { value: number } | null = null;
  /** Each edge material's own colour, so the X-ray can lerp away and back. */
  private readonly edgeColor = new WeakMap<THREE.Material, THREE.Color>();

  private explode = 0;
  private explodeTarget = 0;
  private dusk = 0;
  private duskTarget = 0;
  private xray = 0;
  private xrayTarget = 0;
  private build = BUILD_MAX;
  private buildTarget = BUILD_MAX;
  /** Set while «تشغيل» plays the construction through. */
  private playing = false;
  private isolated: string | null = null;
  private selected: string | null = null;
  /** The unit under a mouse pointer, lifted a little so it reads as clickable. */
  private hovered: string | null = null;
  private hoverAt: THREE.Vector2 | null = null;
  private readonly ray = new THREE.Raycaster();
  private readonly lampHead: THREE.MeshStandardMaterial;
  private readonly ripples: THREE.Texture;
  private readonly pools: THREE.MeshBasicMaterial;
  private readonly towerWindows: THREE.MeshStandardMaterial[];
  /** Warm lights in the courtyard and along the plaza, on only at dusk. */
  private readonly nightLights: THREE.PointLight[] = [];
  private readonly crane = crane();
  /** A faint mint grid on the ground that only the X-ray shows. */
  private readonly grid = new THREE.GridHelper(600, 120, MAT.xray, MAT.xray);
  private flight: {
    t: number;
    fromPos: THREE.Vector3;
    toPos: THREE.Vector3;
    fromTarget: THREE.Vector3;
    toTarget: THREE.Vector3;
  } | null = null;
  private last = performance.now();
  private readonly casting = new Map<string, boolean>();
  private readonly screenRight = new THREE.Vector3();
  /** Null until start(), and stays null if the effects cannot be built. */
  private pipeline: THREE.RenderPipeline | null = null;
  /**
   * The framing last asked for, re-run when the window changes shape so a
   * phone turned sideways still shows the whole building. Dropped the moment
   * the visitor orbits: from then on the camera is theirs.
   */
  private reframe: (() => void) | null = null;

  constructor(
    private readonly host: HTMLElement,
    private readonly building: Building,
    /** Skip WebGPU and use the WebGL 2 backend: how most phones will see it. */
    forceWebGL = false,
  ) {
    this.renderer = new THREE.WebGPURenderer({ antialias: true, alpha: false, forceWebGL });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    // PCFSoft is gone from WebGPURenderer; PCF is what it would fall back to.
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    // Filmic: the warm, slightly compressed highlights of an architectural render.
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.95;
    host.append(this.renderer.domElement);

    this.labels.domElement.className = 'labels';
    host.append(this.labels.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.maxPolarAngle = THREE.MathUtils.degToRad(86);
    this.controls.minDistance = 25;
    this.controls.maxDistance = 900;
    this.controls.addEventListener('start', () => {
      this.controls.autoRotate = false;
      this.flight = null;
      this.reframe = null;
    });

    this.scene.backgroundNode = mix(
      this.skyHorizon,
      this.skyTop,
      smoothstep(0.42, 1.0, screenUV.y.oneMinus()),
    );
    this.scene.fog = this.fog;
    this.scene.add(this.hemi, this.sun, this.sun.target, building.root);

    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(4096, 4096);
    const s = this.sun.shadow.camera;
    s.left = s.bottom = -80;
    s.right = s.top = 80;
    s.near = 1;
    s.far = 450;
    this.sun.shadow.bias = -0.0003;
    this.sun.shadow.normalBias = 0.03;

    const around = surroundings(building.halfW, building.halfD);
    this.scene.add(around.group);
    this.lampHead = around.lampHead;
    this.ripples = around.ripples;
    this.pools = around.pools;
    this.towerWindows = around.towerWindows;
    // Four warm sources washing the courtyard faces and the plaza in front.
    const hd = building.halfD;
    for (const [x, y, z] of [
      [-14, 7, -hd * 0.2],
      [12, 7, -hd * 0.05],
      [0, 8, -hd * 0.6],
    ] as const) {
      const light = new THREE.PointLight(MAT.warmLight, 0, 45, 1.6);
      light.position.set(x, y, z);
      this.nightLights.push(light);
      this.scene.add(light);
    }

    // In the courtyard, where a crane can reach both wings.
    this.crane.group.position.set(-4, 0, -building.halfD * 0.35);
    this.scene.add(this.crane.group);
    const gridMaterial = this.grid.material as THREE.LineBasicMaterial;
    gridMaterial.transparent = true;
    gridMaterial.opacity = 0;
    gridMaterial.depthWrite = false;
    this.grid.position.y = 0.03;
    this.scene.add(this.grid);

    this.bindPicking();
    // Sized now, not at start(), so a preset set before the first frame frames
    // against the real aspect ratio.
    this.resize();
    new ResizeObserver(() => this.resize()).observe(host);
  }

  /** Resolves to the backend actually in use: WebGPU, or its WebGL 2 fallback. */
  async start(): Promise<'WebGPU' | 'WebGL'> {
    await this.renderer.init();
    // A neutral studio for the glass and metal to reflect.
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    // ?fx=0 skips ambient occlusion and bloom: for a slow device, and for testing.
    if (!new URLSearchParams(location.search).has('fx', '0')) this.pipeline = this.buildPipeline();
    this.resize();
    // Every shader compiled before the first frame, in parallel where the
    // driver allows: WebGL otherwise compiles each on first use, on the main
    // thread, and a phone freezes on a half-drawn scene.
    await this.renderer.compileAsync(this.scene, this.camera).catch(() => {});
    this.renderer.setAnimationLoop(() => this.frame());
    const backend = this.renderer.backend as { isWebGPUBackend?: boolean };
    return backend.isWebGPUBackend ? 'WebGPU' : 'WebGL';
  }

  /**
   * A slow turn on arrival, so the page shows it is 3D before anyone touches
   * it. Stops at the first touch, preset or pick, and never runs for a
   * visitor who asked the system for less motion.
   */
  startOrbit() {
    this.controls.autoRotate = !reduceMotion.matches;
    this.controls.autoRotateSpeed = 0.35;
  }

  preset(name: PresetName, instant = false) {
    this.controls.autoRotate = false;
    const p = PRESETS[name];
    this.explodeTarget = p.explode;
    this.duskTarget = p.dusk;
    this.xrayTarget = 0;
    this.isolated = null;
    this.reframe = () => this.flyTo(this.frameAll(p.explode), p.azimuth, p.elevation, p.zoom, true);
    this.flyTo(this.frameAll(p.explode), p.azimuth, p.elevation, p.zoom, instant);
  }

  setExplode(value: number) {
    this.explodeTarget = value;
  }

  get explodeValue() {
    return this.explodeTarget;
  }

  /** «شفّاف» (the X-ray): the building turns to glass with glowing edges; units stay lit. */
  setXray(on: boolean) {
    this.xrayTarget = on ? 1 : 0;
  }

  get xrayOn() {
    return this.xrayTarget > 0.5;
  }

  /** Where the construction slider stands, 0 … BUILD_MAX. */
  setBuild(value: number, instant = false) {
    this.playing = false;
    this.buildTarget = Math.min(BUILD_MAX, Math.max(0, value));
    if (instant) this.build = this.buildTarget;
  }

  /** Plays the construction from the bare site, or from where it stands. */
  playBuild() {
    if (this.buildTarget >= BUILD_MAX - 0.01) this.build = this.buildTarget = 0;
    this.playing = true;
  }

  pauseBuild() {
    this.playing = false;
  }

  get buildValue() {
    return this.buildTarget;
  }

  isolate(floorKey: string | null) {
    this.controls.autoRotate = false;
    this.isolated = floorKey;
    const az = THREE.MathUtils.radToDeg(
      Math.atan2(
        this.camera.position.x - this.controls.target.x,
        this.camera.position.z - this.controls.target.z,
      ),
    );
    if (floorKey === null) {
      const frame = this.frameAll(this.explodeTarget);
      this.reframe = () => this.flyTo(frame, az, 24, 0.95, true);
      this.flyTo(frame, az, 24, 0.95);
      return;
    }
    const floor = this.building.floors.find((f) => f.key === floorKey);
    if (!floor) return;
    const y = this.floorBase(floor.order, this.explodeTarget) + floor.height / 2;
    // On the storey's own extent: the ground floor sits off the drawing's centre.
    const frame = {
      target: new THREE.Vector3(floor.center.x, y, floor.center.z),
      radius: floor.radius,
    };
    this.reframe = () => this.flyTo(frame, az, 50, 1.1, true);
    this.flyTo(frame, az, 50, 1.1);
  }

  select(code: string | null) {
    this.selected = code;
  }

  private floorBase(order: number, explode: number) {
    const floor = this.building.floors[order];
    return (floor?.stackedY ?? 0) + order * EXPLODE_GAP_M * explode;
  }

  private frameAll(explode: number) {
    const height =
      this.building.stackedHeight + (this.building.floors.length - 1) * EXPLODE_GAP_M * explode;
    return {
      target: new THREE.Vector3(0, height * 0.42, 0),
      radius: Math.hypot(this.building.radius, height / 2),
    };
  }

  /** Puts a sphere of `radius` round `target` just inside the narrower field of view. */
  private flyTo(
    frame: { target: THREE.Vector3; radius: number },
    azimuth: number,
    elevation: number,
    zoom: number,
    instant = false,
  ) {
    const vFov = THREE.MathUtils.degToRad(this.camera.fov);
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.camera.aspect);
    // A preset's zoom is tuned on a wide screen, where the overlay leaves the
    // sides free. Portrait has no slack at the sides, so stand a little back.
    const fit = this.camera.aspect < 1 ? Math.max(zoom, 1) * 1.08 : zoom;
    const distance = (frame.radius / Math.sin(Math.min(vFov, hFov) / 2)) * fit;
    const az = THREE.MathUtils.degToRad(azimuth);
    const el = THREE.MathUtils.degToRad(elevation);
    const toPos = new THREE.Vector3(
      frame.target.x + distance * Math.cos(el) * Math.sin(az),
      frame.target.y + distance * Math.sin(el),
      frame.target.z + distance * Math.cos(el) * Math.cos(az),
    );
    if (instant || reduceMotion.matches) {
      this.camera.position.copy(toPos);
      this.controls.target.copy(frame.target);
      this.flight = null;
      return;
    }
    this.flight = {
      t: 0,
      fromPos: this.camera.position.clone(),
      toPos,
      fromTarget: this.controls.target.clone(),
      toTarget: frame.target,
    };
  }

  /**
   * Ambient occlusion and bloom. AO is the soft darkening in corners and
   * under the fascias, the thing that stops flat shading looking like a
   * diagram; a normals pre-pass feeds GTAO at half resolution. Bloom is the
   * glow round lit windows, signs and lamps, and is only turned up at dusk
   * and in the X-ray. Without either the page still renders, just flatter.
   */
  private buildPipeline(): THREE.RenderPipeline | null {
    try {
      const pipeline = new THREE.RenderPipeline(this.renderer);
      // Single-sampled: GTAO gathers from depth, which it cannot do multisampled.
      const prePass = pass(this.scene, this.camera, { samples: 0 });
      prePass.setMRT(mrtNormals());
      const occlusion = ao(prePass.getTextureNode('depth'), prePass.getTextureNode(), this.camera);
      occlusion.resolutionScale = 0.5;
      occlusion.radius.value = 1.8;
      occlusion.scale.value = 1.2;
      const scenePass = pass(this.scene, this.camera);
      scenePass.contextNode = builtinAOContext(occlusion.getTextureNode().sample(screenUV).r);
      const color = scenePass.getTextureNode('output');
      const glow = bloom(color, 0, 0.5, 0.78);
      this.bloomStrength = glow.strength as unknown as { value: number };
      this.bloomThreshold = glow.threshold as unknown as { value: number };
      pipeline.outputNode = color.add(glow);
      return pipeline;
    } catch (error) {
      console.warn('post-processing unavailable', error);
      return null;
    }
  }

  private resize() {
    const { clientWidth: w, clientHeight: h } = this.host;
    if (!w || !h) return;
    this.camera.aspect = w / h;
    // A portrait phone through a 32° lens has a 15° horizontal field: the
    // building shrinks to a speck far away. Widen the lens instead.
    this.camera.fov = this.camera.aspect < 1 ? 48 : 32;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
    this.labels.setSize(w, h);
    this.reframe?.();
  }

  /** What is under a point on the canvas, among floors that are showing. */
  private pickAt(ndc: THREE.Vector2): Pick | null {
    this.ray.setFromCamera(ndc, this.camera);
    const live = new Set(this.building.floors.filter((f) => f.presence > 0.6).map((f) => f.key));
    const hit = this.ray
      .intersectObjects(this.building.pickable, false)
      .find((h) => live.has(h.object.userData.floor as string));
    if (!hit) return null;
    const { floor, code } = hit.object.userData as { floor: string; code?: string };
    return code ? { floor, code } : { floor };
  }

  private bindPicking() {
    const el = this.renderer.domElement;
    let down: { x: number; y: number } | null = null;
    const toNdc = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      return new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      );
    };

    el.addEventListener('pointerdown', (e) => (down = { x: e.clientX, y: e.clientY }));
    el.addEventListener('pointerup', (e) => {
      // A drag is an orbit, not a click.
      if (!down || Math.hypot(e.clientX - down.x, e.clientY - down.y) > 6) return;
      down = null;
      this.onPick(this.pickAt(toNdc(e)));
    });
    // Hover is a mouse affordance; on touch it would only flicker.
    el.addEventListener('pointermove', (e) => {
      if (e.pointerType === 'mouse' && e.buttons === 0) this.hoverAt = toNdc(e);
    });
    el.addEventListener('pointerleave', () => {
      this.hoverAt = null;
      this.hovered = null;
      el.style.cursor = '';
    });

    // Labels sit in their own layer above the canvas, so they pick themselves.
    this.labels.domElement.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const unit = target.closest<HTMLElement>('[data-code]');
      const floor = target.closest<HTMLElement>('[data-floor]');
      if (unit?.dataset.code) {
        const handle = this.building.units.get(unit.dataset.code);
        if (handle) this.onPick({ floor: handle.floorKey, code: handle.unit.code });
      } else if (floor?.dataset.floor) {
        this.onPick({ floor: floor.dataset.floor });
      }
    });
  }

  private frame() {
    const now = performance.now();
    const dt = Math.min((now - this.last) / 1000, 0.1);
    this.last = now;

    this.explode = approach(this.explode, this.explodeTarget, dt, 5);
    this.dusk = approach(this.dusk, this.duskTarget, dt, 3);
    this.xray = approach(this.xray, this.xrayTarget, dt, 4);
    if (this.playing) {
      // A full build in about eleven seconds: long enough to watch each stage.
      this.buildTarget = Math.min(BUILD_MAX, this.buildTarget + dt * 0.36);
      this.build = this.buildTarget;
      if (this.buildTarget >= BUILD_MAX) this.playing = false;
      this.onBuild(this.buildTarget, this.playing);
    } else {
      this.build = approach(this.build, this.buildTarget, dt, 6);
    }

    if (this.flight) {
      const f = this.flight;
      f.t = Math.min(1, f.t + dt / 1.3);
      const k = ease(f.t);
      this.camera.position.lerpVectors(f.fromPos, f.toPos, k);
      this.controls.target.lerpVectors(f.fromTarget, f.toTarget, k);
      if (f.t >= 1) this.flight = null;
    }
    this.controls.update();

    // At most one hover test a frame, however fast the mouse moves.
    if (this.hoverAt) {
      const pick = this.pickAt(this.hoverAt);
      this.hoverAt = null;
      this.hovered = pick?.code ?? null;
      this.renderer.domElement.style.cursor = pick ? 'pointer' : '';
    }

    // The crane works while the frame and envelope go up, and leaves for fit-out.
    const onSite = 1 - clamp01((this.build - 3.2) / 0.5);
    const arrived = clamp01(this.build / 0.4);
    const craneOpacity = onSite * arrived;
    this.crane.group.visible = craneOpacity > 0.01;
    for (const m of this.crane.materials) m.opacity = craneOpacity;
    this.crane.slew.rotation.y = Math.sin(now / 5200) * 1.1 + 0.6;
    (this.grid.material as THREE.LineBasicMaterial).opacity = this.xray * 0.22;
    this.grid.visible = this.xray > 0.01;

    this.ripples.offset.x += dt * 0.004;
    this.ripples.offset.y += dt * 0.0025;

    this.applyLight();
    this.applyFloors(dt);

    if (this.pipeline) this.pipeline.render();
    else this.renderer.render(this.scene, this.camera);
    this.labels.render(this.scene, this.camera);
  }

  private applyLight() {
    const d = this.dusk;
    const x = this.xray;
    const p = this.palette;
    const { top, horizon, fog } = this.scratch;
    top.lerpColors(p.dayTop, p.duskTop, d).lerp(p.xrayTop, x);
    horizon.lerpColors(p.dayHorizon, p.duskHorizon, d).lerp(p.xrayHorizon, x);
    fog.lerpColors(p.dayFog, p.duskFog, d).lerp(p.xrayFog, x);
    this.skyTop.value.copy(top);
    this.skyHorizon.value.copy(horizon);
    this.fog.color.copy(fog);
    this.fog.density = lerp(0.0024, 0.0034, Math.max(d, x));

    // The X-ray dims the world round the building so the glowing edges carry.
    this.hemi.intensity = lerp(lerp(0.85, 0.07, d), 0.16, x);
    this.scene.environmentIntensity = lerp(lerp(0.45, 0.035, d), 0.05, x);
    this.sun.intensity = lerp(lerp(3.2, 0.16, d), 0.22, x);
    this.sun.color.lerpColors(p.sunDay, p.sunDusk, d);
    this.lampHead.emissiveIntensity = 3.2 * d;
    if (this.bloomStrength) this.bloomStrength.value = Math.max(d * 1.15, x * 0.9);
    if (this.bloomThreshold) this.bloomThreshold.value = lerp(0.8, 0.5, d);
    this.pools.opacity = d * (1 - x);
    for (const light of this.nightLights) light.intensity = d * (1 - x) * 70;
    for (const material of this.towerWindows) material.emissiveIntensity = d * 1.3;
    // Night is darker than the day's exposure would make it.
    this.renderer.toneMappingExposure = lerp(0.95, 0.82, d);
    // Day: high and to the hero camera's left, lighting the glazed side with
    // long shadows falling where they show. Dusk: low and warm, behind.
    const az = THREE.MathUtils.degToRad(lerp(-95, 60, d));
    const el = THREE.MathUtils.degToRad(lerp(42, 6, d));
    this.sun.position.set(
      180 * Math.cos(el) * Math.sin(az),
      180 * Math.sin(el),
      180 * Math.cos(el) * Math.cos(az),
    );
    this.sun.target.position.set(0, 0, 0);
  }

  /**
   * One material's opacity: its own, times its floor's presence, times how
   * far its construction stage has got on that floor, times the X-ray.
   * Floors go up bottom first, each a little after the one below.
   */
  private applyFaded(entry: Faded, presence: number, order: number) {
    const { material, base, noDepth, stage, role, finish } = entry;
    const reveal = clamp01((this.build - (stage - 1) - order * 0.2) / 0.5);
    const x = this.xray;
    let opacity = base * presence * reveal;
    if (role === 'solid') opacity *= lerp(1, 0.07, x);
    else if (role === 'glass') opacity *= lerp(1, 0.08, x);
    else if (role === 'detail' || role === 'light') opacity *= lerp(1, 0, x);
    else if (role === 'unit') {
      // A let room's air fills with warm light at dusk; the X-ray outlines it.
      const lit = base > 0.1 ? base : lerp(base, 0.34, this.dusk * (1 - x));
      opacity = presence * reveal * lerp(lit, base > 0.1 ? 0.5 : 0.07, x);
    } else if (role === 'edge') {
      opacity = presence * reveal * lerp(base, 0.75, x);
      const line = material as THREE.LineBasicMaterial;
      let own = this.edgeColor.get(material);
      if (!own) this.edgeColor.set(material, (own = line.color.clone()));
      line.color.lerpColors(own, this.palette.xrayEdge, x);
    }
    if (finish) {
      // Raw concrete until the envelope reaches this floor, then its stone.
      const clad = clamp01((this.build - 2 - order * 0.2) / 0.5);
      (material as THREE.MeshStandardMaterial).color.lerpColors(finish.raw, finish.done, clad);
    }
    material.opacity = opacity;
    material.visible = opacity > 0.003;
    material.depthWrite = !noDepth && presence > 0.95 && reveal > 0.99 && x < 0.05;
  }

  private applyFloors(dt: number) {
    this.screenRight.setFromMatrixColumn(this.camera.matrixWorld, 0);
    const finished = this.build > BUILD_MAX - 0.4;
    const showUnitLabels = (key: string) =>
      finished &&
      (this.isolated === key ||
        (this.isolated === null && (this.explode > 0.55 || this.xray > 0.5) && !narrow.matches));

    for (const floor of this.building.floors) {
      const iso = this.isolated;
      const above =
        iso !== null && floor.order > (this.building.floors.find((f) => f.key === iso)?.order ?? 0);
      const target = iso === null || iso === floor.key ? 1 : above ? 0 : 0.12;
      floor.presence = approach(floor.presence, target, dt, 7);
      // Not started yet in the construction: the floor is not there at all.
      const begun = this.build > 1 + floor.order * 0.2 || floor.order === 0;
      floor.group.visible = floor.presence > 0.01 && (begun || this.build > 0.01);
      // A ghosted floor, or one still in X-ray glass, casts no shadow.
      const solid = floor.presence > 0.5 && this.xray < 0.5;
      if (this.casting.get(floor.key) !== solid) {
        this.casting.set(floor.key, solid);
        floor.group.traverse((o) => {
          if ((o as THREE.Mesh).isMesh) o.castShadow = solid;
        });
      }
      floor.group.position.y = this.floorBase(floor.order, this.explode);
      for (const entry of floor.faded) this.applyFaded(entry, floor.presence, floor.order);

      // The tag rides the corner furthest to the screen's right, so it never
      // sits on top of the unit labels whichever way the camera has turned.
      let best = -Infinity;
      for (const [x, z] of floor.corners) {
        const d = x * this.screenRight.x + z * this.screenRight.z;
        if (d > best) {
          best = d;
          floor.tag.position.set(
            x + this.screenRight.x * 5,
            floor.height / 2,
            z + this.screenRight.z * 5,
          );
        }
      }
      floor.tag.element.classList.toggle(
        'is-hidden',
        // On a phone the floor chips already name the floors, and a tag at
        // the screen-right corner would run off the edge.
        !(this.isolated === null && this.explode > 0.3 && !narrow.matches),
      );
    }

    const roof = this.building.roof;
    const roofTarget = this.isolated !== null ? 0 : 1 - Math.min(1, this.explode * 1.6);
    roof.presence = approach(roof.presence, roofTarget, dt, 7);
    roof.group.visible = roof.presence > 0.01;
    roof.group.position.y =
      (this.building.floors.at(-1)?.height ?? 0) + this.explode * EXPLODE_GAP_M * 1.4;
    const roofOrder = this.building.floors.length - 1 + 0.4;
    for (const entry of roof.faded) this.applyFaded(entry, roof.presence, roofOrder);

    // Interiors light up at dusk: warm where let, mint where free. A little
    // even by day, as a lit restaurant behind glass reads.
    for (const { material, available, kind } of this.building.lights) {
      material.emissiveIntensity =
        kind === 'panel'
          ? (available ? 0.35 : 0.25) + this.dusk * (available ? 1.6 : 2.2)
          : kind === 'led'
            ? this.dusk * 6 * (1 - this.xray)
            : this.dusk * (available ? 0.5 : 0.6) * (1 - this.xray);
    }
    // And the glazing carries a little of that light out, as lit windows do.
    for (const material of this.building.glazing) material.emissiveIntensity = this.dusk * 0.6;

    for (const handle of this.building.units.values()) {
      const floor = this.building.floors.find((f) => f.key === handle.floorKey);
      const presence = floor?.presence ?? 0;
      const selected = this.selected === handle.unit.code;
      const available = handle.unit.status === 'available';
      const hovered = this.hovered === handle.unit.code && !selected;
      // The room's air: mint where free, brighter at dusk and in the X-ray.
      // At night a let room is a lit box behind its glass; a free one glows mint.
      const glow =
        (available ? 0.5 * Math.max(this.dusk, this.xray) : 1.4 * this.dusk * (1 - this.xray)) +
        (selected ? 0.6 : 0) +
        (hovered ? 0.3 : 0);
      for (const material of handle.materials) {
        material.emissive.copy(available ? this.palette.glowAvailable : this.palette.glowLeased);
        material.emissiveIntensity = glow;
      }
      const el = handle.label.element;
      el.classList.toggle('is-selected', selected);
      el.classList.toggle(
        'is-hidden',
        presence < 0.9 || !(showUnitLabels(handle.floorKey) || (selected && finished)),
      );
    }
  }
}
